import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, ShieldIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
// Mock roles data
const rolesData = [{
  id: 1,
  name: 'Admin',
  description: 'Full system access',
  usersCount: 8,
  permissions: {
    dashboard: ['view'],
    merchants: ['view', 'create', 'edit', 'delete'],
    users: ['view', 'create', 'edit', 'delete'],
    transactions: ['view', 'export'],
    reports: ['view', 'export', 'schedule'],
    settings: ['view', 'edit']
  }
}, {
  id: 2,
  name: 'Manager',
  description: 'Manage merchants and view reports',
  usersCount: 15,
  permissions: {
    dashboard: ['view'],
    merchants: ['view', 'create', 'edit'],
    users: ['view'],
    transactions: ['view', 'export'],
    reports: ['view', 'export'],
    settings: ['view']
  }
}, {
  id: 3,
  name: 'Agent',
  description: 'Create merchants and view transactions',
  usersCount: 42,
  permissions: {
    dashboard: ['view'],
    merchants: ['view', 'create'],
    users: [],
    transactions: ['view'],
    reports: ['view'],
    settings: []
  }
}, {
  id: 4,
  name: 'Auditor',
  description: 'View and export reports and transactions',
  usersCount: 6,
  permissions: {
    dashboard: ['view'],
    merchants: ['view'],
    users: ['view'],
    transactions: ['view', 'export'],
    reports: ['view', 'export', 'schedule'],
    settings: []
  }
}];
interface Role {
  id: number;
  name: string;
  description: string;
  usersCount: number;
  permissions: {
    [key: string]: string[];
  };
}
const RoleManagement: React.FC = () => {
  const [roles] = useState<Role[]>(rolesData);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: ''
  });
  const handleCreateRole = () => {
    setEditForm({
      name: '',
      description: ''
    });
    setShowCreateModal(true);
  };
  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setEditForm({
      name: role.name,
      description: role.description
    });
    setShowEditModal(true);
  };
  const handleDeleteRole = (role: Role) => {
    setSelectedRole(role);
    setShowDeleteModal(true);
  };
  const handleViewPermissions = (role: Role) => {
    setSelectedRole(role);
    setShowPermissionsModal(true);
  };
  const confirmDelete = () => {
    // Delete logic would go here
    setShowDeleteModal(false);
    setSelectedRole(null);
  };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCreateSubmit = () => {
    // Create logic would go here
    setShowCreateModal(false);
  };
  const handleEditSubmit = () => {
    // Edit logic would go here
    setShowEditModal(false);
    setSelectedRole(null);
  };
  const columns = [{
    header: 'Role Name',
    accessor: 'name',
    sortable: true
  }, {
    header: 'Description',
    accessor: 'description',
    sortable: true
  }, {
    header: 'Users',
    accessor: 'usersCount',
    sortable: true
  }, {
    header: 'Permissions',
    accessor: (role: Role) => {
      const permissionCount = Object.values(role.permissions).flat().length;
      return <button onClick={() => handleViewPermissions(role)} className="text-blue-600 dark:text-blue-400 hover:underline" data-id="element-6616">
            {permissionCount} permissions
          </button>;
    },
    sortable: false
  }];
  return <div className="space-y-6" data-id="element-6617">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-6618">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-6619">
          Role Management
        </h1>
        <div className="mt-3 sm:mt-0" data-id="element-6620">
          <button onClick={handleCreateRole} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-6621">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-6622" />
            Create Role
          </button>
        </div>
      </div>
      <Card data-id="element-6623">
        <Table columns={columns} data={roles} keyField="id" actions={role => <div className="flex justify-end space-x-2" data-id="element-6625">
              <button onClick={() => handleEditRole(role)} className="p-1 rounded-full text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30" title="Edit" data-id="element-6626">
                <EditIcon className="h-5 w-5" data-id="element-6627" />
              </button>
              <button onClick={() => handleDeleteRole(role)} className="p-1 rounded-full text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="Delete" disabled={role.name === 'Admin'} // Prevent deleting Admin role
        data-id="element-6628">
                <TrashIcon className={`h-5 w-5 ${role.name === 'Admin' ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-6629" />
              </button>
            </div>} data-id="element-6624" />
      </Card>
      {/* Create Role Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create New Role" footer={<>
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6631">
              Cancel
            </button>
            <button onClick={handleCreateSubmit} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-6632">
              Create
            </button>
          </>} data-id="element-6630">
        <div className="space-y-4" data-id="element-6633">
          <div data-id="element-6634">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6635">
              Role Name <span className="text-red-500" data-id="element-6636">*</span>
            </label>
            <input type="text" name="name" value={editForm.name} onChange={handleFormChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter role name" required data-id="element-6637" />
          </div>
          <div data-id="element-6638">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6639">
              Description
            </label>
            <textarea name="description" value={editForm.description} onChange={handleFormChange} rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter role description" data-id="element-6640" />
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded p-3" data-id="element-6641">
            <p className="text-sm text-blue-800 dark:text-blue-300" data-id="element-6642">
              After creating the role, you will be able to define its
              permissions.
            </p>
          </div>
        </div>
      </Modal>
      {/* Edit Role Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Role" footer={<>
            <button onClick={() => setShowEditModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6644">
              Cancel
            </button>
            <button onClick={handleEditSubmit} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-6645">
              Save Changes
            </button>
          </>} data-id="element-6643">
        <div className="space-y-4" data-id="element-6646">
          <div data-id="element-6647">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6648">
              Role Name <span className="text-red-500" data-id="element-6649">*</span>
            </label>
            <input type="text" name="name" value={editForm.name} onChange={handleFormChange} className={`w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm ${selectedRole?.name === 'Admin' ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`} placeholder="Enter role name" required disabled={selectedRole?.name === 'Admin'} data-id="element-6650" />
            {selectedRole?.name === 'Admin' && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-6651">
                The Admin role name cannot be changed.
              </p>}
          </div>
          <div data-id="element-6652">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6653">
              Description
            </label>
            <textarea name="description" value={editForm.description} onChange={handleFormChange} rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter role description" data-id="element-6654" />
          </div>
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-6655">
            <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => {
            setShowEditModal(false);
            setTimeout(() => {
              setShowPermissionsModal(true);
            }, 100);
          }} data-id="element-6656">
              <ShieldIcon className="h-4 w-4 mr-2" data-id="element-6657" />
              Edit Permissions
            </button>
          </div>
        </div>
      </Modal>
      {/* View/Edit Permissions Modal */}
      <Modal isOpen={showPermissionsModal} onClose={() => setShowPermissionsModal(false)} title={`${selectedRole?.name} Permissions`} size="lg" footer={<>
            <button onClick={() => setShowPermissionsModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6659">
              Cancel
            </button>
            <button onClick={() => setShowPermissionsModal(false)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-6660">
              Save Permissions
            </button>
          </>} data-id="element-6658">
        {selectedRole && <div className="space-y-4" data-id="element-6661">
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6662">
              Configure permissions for the {selectedRole.name} role. Changes
              will affect all users with this role.
            </p>
            <div className="overflow-x-auto" data-id="element-6663">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6664">
                <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-6665">
                  <tr data-id="element-6666">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6667">
                      Module
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6668">
                      View
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6669">
                      Create
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6670">
                      Edit
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6671">
                      Delete
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6672">
                      Export
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6673">
                      Schedule
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6674">
                  {Object.keys(selectedRole.permissions).map(module => <tr key={module} data-id="element-6675">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-6676">
                        {module.charAt(0).toUpperCase() + module.slice(1)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6677">
                        <input type="checkbox" checked={selectedRole.permissions[module].includes('view')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" disabled={selectedRole.name === 'Admin'} data-id="element-6678" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6679">
                        {['dashboard', 'settings'].includes(module) ? <span className="text-gray-300 dark:text-gray-600" data-id="element-6680">
                            N/A
                          </span> : <input type="checkbox" checked={selectedRole.permissions[module].includes('create')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" disabled={selectedRole.name === 'Admin'} data-id="element-6681" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6682">
                        {['dashboard', 'reports'].includes(module) ? <span className="text-gray-300 dark:text-gray-600" data-id="element-6683">
                            N/A
                          </span> : <input type="checkbox" checked={selectedRole.permissions[module].includes('edit')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" disabled={selectedRole.name === 'Admin'} data-id="element-6684" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6685">
                        {['dashboard', 'transactions', 'reports', 'settings'].includes(module) ? <span className="text-gray-300 dark:text-gray-600" data-id="element-6686">
                            N/A
                          </span> : <input type="checkbox" checked={selectedRole.permissions[module].includes('delete')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" disabled={selectedRole.name === 'Admin'} data-id="element-6687" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6688">
                        {['dashboard', 'users', 'settings'].includes(module) ? <span className="text-gray-300 dark:text-gray-600" data-id="element-6689">
                            N/A
                          </span> : <input type="checkbox" checked={selectedRole.permissions[module].includes('export')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" disabled={selectedRole.name === 'Admin'} data-id="element-6690" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6691">
                        {module === 'reports' ? <input type="checkbox" checked={selectedRole.permissions.reports.includes('schedule')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" disabled={selectedRole.name === 'Admin'} data-id="element-6692" /> : <span className="text-gray-300 dark:text-gray-600" data-id="element-6693">
                            N/A
                          </span>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {selectedRole.name === 'Admin' && <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded p-3" data-id="element-6694">
                <p className="text-sm text-yellow-800 dark:text-yellow-300" data-id="element-6695">
                  The Admin role has full system access by default and its
                  permissions cannot be modified.
                </p>
              </div>}
          </div>}
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Deletion" footer={<>
            <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6697">
              Cancel
            </button>
            <button onClick={confirmDelete} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-6698">
              Delete
            </button>
          </>} data-id="element-6696">
        <p className="text-gray-700 dark:text-gray-300" data-id="element-6699">
          Are you sure you want to delete the "{selectedRole?.name}" role? This
          will affect {selectedRole?.usersCount} users who currently have this
          role assigned.
        </p>
        <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded p-3" data-id="element-6700">
          <p className="text-sm text-yellow-800 dark:text-yellow-300" data-id="element-6701">
            You will need to reassign users to a different role before deleting
            this role.
          </p>
        </div>
      </Modal>
    </div>;
};
export default RoleManagement;